import { Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useOnboardingRuns } from "@/api/hooks";
import type { OnboardingRunStatus } from "@/api/types";
import { Layout } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function StatusPill({ status }: { status: OnboardingRunStatus }) {
	switch (status) {
		case "succeeded":
			return <Badge variant="success">Succeeded</Badge>;
		case "running":
			return <Badge variant="outline">Running</Badge>;
		case "failed":
			return <Badge variant="destructive">Failed</Badge>;
		case "aborted":
			return (
				<Badge variant="outline" className="text-muted-foreground">
					Aborted
				</Badge>
			);
		default:
			return (
				<Badge variant="outline" className="text-muted-foreground">
					Pending
				</Badge>
			);
	}
}

export function OnboardingListPage() {
	const runs = useOnboardingRuns();
	return (
		<Layout>
			<div className="mx-auto max-w-4xl space-y-6">
				<header className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-semibold tracking-tight">Onboarding runs</h2>
						<p className="text-sm text-muted-foreground">Every wizard attempt, newest first.</p>
					</div>
					<Button asChild>
						<Link to="/onboarding/new">
							<Plus className="mr-1 h-4 w-4" aria-hidden />
							New client
						</Link>
					</Button>
				</header>

				{runs.isLoading ? (
					<p className="text-sm text-muted-foreground">Loading…</p>
				) : runs.error ? (
					<p className="text-sm text-destructive">
						{runs.error instanceof ApiError ? runs.error.detail : "Load failed."}
					</p>
				) : (runs.data?.runs.length ?? 0) === 0 ? (
					<Card>
						<CardHeader>
							<CardTitle>No runs yet</CardTitle>
							<CardDescription>
								Kick off your first client onboarding via the button above.
							</CardDescription>
						</CardHeader>
					</Card>
				) : (
					<div className="space-y-2">
						{runs.data?.runs.map((run) => (
							<Card key={run.id} className="transition-shadow hover:shadow-sm">
								<CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
									<div className="space-y-1">
										<Link
											to={`/onboarding/${run.id}`}
											className="font-medium hover:underline decoration-dotted underline-offset-4"
										>
											{run.display_name}
										</Link>
										<p className="text-xs text-muted-foreground">
											<span className="font-mono">{run.slug}</span> ·{" "}
											{new Date(run.created_at).toLocaleString()}
										</p>
										{run.error_message ? (
											<p className="max-w-xl truncate text-xs text-destructive">
												{run.error_message}
											</p>
										) : null}
									</div>
									<StatusPill status={run.status} />
								</CardContent>
							</Card>
						))}
					</div>
				)}
			</div>
		</Layout>
	);
}
